import { Extension } from '@tiptap/core'
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'

/**
 * De selectie van de editor gelijkzetten met wat de browser geselecteerd heeft (R-73, R-140).
 *
 * De browser meldt een gewijzigde selectie met `selectionchange`, en dat is een taak voor later en geen
 * onderdeel van de toetsaanslag zelf. ProseMirror leest de selectie pas bij die melding, en alleen zolang
 * de focus in het vlak staat (`onSelectionChange` in `prosemirror-view`). Gaat de focus eerder weg, met
 * Shift+Tab naar de balk, dan valt de laatste stap van de selectie weg: de browser toont " de stad"
 * geselecteerd, de editor houdt "de stad", en de opmerking krijgt het verkeerde citaat en een bereik dat
 * een teken te kort is. Een snelle invuller en een drukke browser lopen daar allebei tegenaan; in de
 * testsuite gaf het twee wankele tests (`richtextThreads.test.tsx`, `formPanels.test.tsx`).
 *
 * Alleen een tekstselectie wordt overgenomen: een geselecteerde formule of figuur is een knoopselectie, en
 * die tekent de browser anders dan ProseMirror haar bedoelt.
 */
export function syncSelectionFromDom(view: EditorView): void {
  if (!(view.state.selection instanceof TextSelection)) return
  const selection = view.dom.ownerDocument.getSelection()
  const { anchorNode, focusNode } = selection ?? {}
  if (selection === null || anchorNode == null || focusNode == null) return
  if (!view.dom.contains(anchorNode) || !view.dom.contains(focusNode)) return
  let anchor: number
  let head: number
  try {
    anchor = view.posAtDOM(anchorNode, selection.anchorOffset)
    head = view.posAtDOM(focusNode, selection.focusOffset)
  } catch {
    // Een punt buiten de inhoud van de editor (een decoratie, een widget): dan geldt wat de editor al had.
    return
  }
  const current = view.state.selection
  if (current.anchor === anchor && current.head === head) return
  const { doc } = view.state
  view.dispatch(view.state.tr.setSelection(TextSelection.between(doc.resolve(anchor), doc.resolve(head))))
}

/** Bij het verlaten van het vlak eerst de selectie van de browser overnemen, vóór ProseMirror de focus loslaat. */
export const DomSelectionSync = Extension.create({
  name: 'selectieBijVerlaten',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('selectieBijVerlaten'),
        props: {
          handleDOMEvents: {
            blur: (view) => {
              syncSelectionFromDom(view)
              return false
            },
          },
        },
      }),
    ]
  },
})
